import { useEffect, useState } from "react";
import { CalendarClock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { dmy, errMsg } from "./kpiFormat";

const SHORT = { acuity: "Acuity", stripe: "Stripe", enquiries: "Enquiries" };
const SOURCE_LABEL = { acuity: "Acuity (orders, packs, appointments)", stripe: "Stripe (memberships)", enquiries: "Enquiries inbox (leads)" };

const RunState = ({ s }) => (
  <span className={cn("text-2xs font-semibold uppercase tracking-[0.12em]",
    s === "ok" ? "text-emerald-700" : s === "failed" ? "text-red-600" : "text-zinc-500")}>{s || "never"}</span>
);

export const SourcesPanel = ({ canRunFriday, nextFriday }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/kpi/sources").then((r) => setSt(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const act = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(msg);
      setTimeout(load, 4000);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!st) return <div className="h-40 animate-pulse rounded-lg bg-zinc-100" />;
  const csv = Object.entries(st.last_runs).filter(([k]) => k.startsWith("csv:"));
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6 space-y-5" data-testid="kpi-sources-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Data sources</h3>
          <p className="text-sm text-zinc-500">Everything syncs automatically every night at 2:00am Melbourne, and again before the Saturday draft. Sync now to refresh straight away.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act("/kpi/sync", "Sync started — this can take a minute")}
            data-testid="kpi-sync-btn"><RefreshCw className="h-4 w-4 mr-1.5" />Sync now</Button>
          {canRunFriday && <Button size="sm" variant="outline" disabled={busy}
            onClick={() => act("/kpi/friday/run-now", "Weekly draft started — you'll get a notification when it's ready")}
            data-testid="kpi-friday-run-btn"><CalendarClock className="h-4 w-4 mr-1.5" />Run weekly draft now</Button>}
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {Object.entries(SOURCE_LABEL).map(([k, label]) => {
          const run = st.last_runs[k];
          const connected = k === "acuity" ? st.acuity_connected : k === "stripe" ? st.stripe_connected : true;
          return (
            <div key={k} className="rounded-md border border-zinc-200 p-3 text-sm" data-testid={`kpi-source-${k}`}>
              <div className="font-medium">{label}</div>
              <div className="mt-1 text-xs text-zinc-500">{connected ? "Connected" : "Not connected — use CSV import"}</div>
              <div className="mt-2 flex items-center gap-2 text-xs"><RunState s={run?.status} />{run && <span className="text-zinc-500">{fmtDateTime(run.finished_at || run.started_at)}</span>}</div>
              {run?.error && <div className="mt-1 text-xs text-red-600">{run.error}</div>}
            </div>
          );
        })}
      </div>
      <div className="grid gap-2 sm:grid-cols-4 text-sm" data-testid="kpi-source-counts">
        {Object.entries(st.counts).map(([k, v]) => (
          <div key={k} className="rounded-md bg-zinc-50 px-3 py-2"><div className="text-2xs uppercase tracking-[0.18em] text-zinc-500">{k}</div>
            <div className="font-mono-num text-lg">{v.toLocaleString("en-AU")}</div></div>
        ))}
      </div>
      {csv.length > 0 && (
        <div className="text-xs text-zinc-600 space-y-1" data-testid="kpi-csv-runs">
          {csv.map(([k, r]) => <div key={k}><b>{k.replace("csv:", "CSV ")}</b> · {r.counts?.rows} rows · {r.by} · {fmtDateTime(r.finished_at)} · “{r.reason}”</div>)}
        </div>
      )}
      <div className="border-t border-zinc-100 pt-4 text-xs text-zinc-600 space-y-1" data-testid="kpi-nightly-runs">
        <div className="font-semibold text-zinc-800">Nightly sync — every night 2:00am Melbourne (an open draft for the last finished week is refreshed too; failures alert Directors and Managers)</div>
        {(st.nightly_runs || []).length === 0 ? <div>No runs yet.</div> : st.nightly_runs.map((r) => (
          <div key={r.run_id} data-testid={`kpi-nightly-run-${r.run_id}`}><RunState s={r.status} /> {fmtDateTime(r.started_at)}
            {r.sync ? ` · ${r.sync.map((x) => `${SHORT[x.source] || x.source} ${x.status}`).join(", ")}` : ""}
            {r.refreshed_week ? ` · draft ${dmy(r.refreshed_week, true)} refreshed` : ""}
            {r.alerted ? ` · ${r.alerted.in_app} alerted, ${r.alerted.emails_sent} email(s) sent` : ""}{r.error ? ` · ${r.error}` : ""}</div>
        ))}
      </div>
      <div className="border-t border-zinc-100 pt-4 text-xs text-zinc-600 space-y-1" data-testid="kpi-friday-runs">
        <div className="font-semibold text-zinc-800">Weekly draft schedule — every Saturday 7:00am Melbourne (next {dmy(nextFriday, true)})</div>
        {st.friday_runs.length === 0 ? <div>No runs yet.</div> : st.friday_runs.map((r) => (
          <div key={r.run_id}><RunState s={r.status} /> {fmtDateTime(r.started_at)} · week {r.week ? dmy(r.week, true) : "—"}
            {r.reason ? ` · ${r.reason}` : ""}{r.notified ? ` · ${r.notified.in_app} alerted, ${r.notified.emails_sent} email(s) sent` : ""}{r.error ? ` · ${r.error}` : ""}</div>
        ))}
      </div>
    </section>
  );
};
